import { useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router-dom';
import clsx from 'clsx';

import { useIsEntitled, useStudentRedirect } from 'features/authorization';
import {
  useAmpNavV2,
  useInitializeServices,
  useMouseDetect,
  useNavigationApiDataPassing,
  useNetworkStatus,
  useScrollToTop,
} from 'shared/lib/utils';
import { AuthService } from 'shared/lib/keycloak';
import { useAssets } from 'shared/api/retail';
import { ConnectionLostError } from 'shared/ui/errors';
import { Spinner } from 'shared/ui/spinner';
import { ReadOnlyBanner } from 'shared/ui/read-only-banner';
import { Navigation } from 'shared/ui/navigation';
import { PAGES } from 'shared/config/constants';
import { getAmpConfigFlags } from 'shared/config/amp-config';
import {
  AvatarBuilderPage,
  RecommendationsPage,
  StreamPage,
  ScheduledAssignmentsPage,
  HomePage,
} from 'pages';

import './index.scss';

const App = () => {
  const appRef = useRef<HTMLDivElement>(null);

  useInitializeServices();
  useScrollToTop();
  useStudentRedirect();
  useMouseDetect(appRef);

  const isOnline = useNetworkStatus();
  const { isLoading } = useIsEntitled();
  const { userAssets } = useAssets();

  useAmpNavV2();
  // do not call it outside of ErrorBoundary as it includes queries call
  // call it after useAmpNavV2 only
  useNavigationApiDataPassing();

  const { isNewNavigationEnabled } = getAmpConfigFlags();

  const isOnAvatarBuilderPage = window.location.href.endsWith(PAGES.AVATAR);

  useEffect(() => {
    const loadingScreen = document.getElementById('loading-spinner');
    if (!loadingScreen) {
      return;
    }
    loadingScreen.style.display = 'none';
  }, []);

  if (!isOnline) {
    return <ConnectionLostError />;
  }

  return (
    <div
      ref={appRef}
      className={clsx('d-flex flex-column', {
        app: isNewNavigationEnabled && !isOnAvatarBuilderPage,
        'read-only': AuthService.isReadOnly(),
      })}
      data-testid="app"
    >
      {isLoading ? (
        <Spinner
          classNames={clsx({
            'app-spinner-new-nav': isNewNavigationEnabled,
          })}
          testId="app-spinner"
          isDark
          isLarge
        />
      ) : (
        <>
          {!isNewNavigationEnabled && <Navigation userAssets={userAssets} />}
          {isNewNavigationEnabled &&
            !isOnAvatarBuilderPage &&
            AuthService.isReadOnly() && <ReadOnlyBanner />}
          <Routes>
            <Route index element={<HomePage />} />
            <Route path={`/${PAGES.AVATAR}`} element={<AvatarBuilderPage />} />
            <Route
              path={`/${PAGES.RECOMMENDATIONS}`}
              element={<RecommendationsPage />}
            />
            <Route path={`/${PAGES.STREAM}`} element={<StreamPage />} />
            <Route
              path={`/${PAGES.SCHEDULED}`}
              element={<ScheduledAssignmentsPage />}
            />
          </Routes>
        </>
      )}
    </div>
  );
};

export default App;
